import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { User } from "@supabase/supabase-js";

import { getUserRole, isAdminUser, isStaffUser } from "@/types/auth";
import { findStaffProfileByEmail, syncStaffRole } from "@/core/repositories/auth.repository";
import type { StaffProfile } from "@/core/services/types/team.types";
import { getSupabaseClient } from "@/lib/supabase";

interface AuthContextValue {
  user: User | null;
  profile: StaffProfile | null;
  isLoading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  useEffect(() => {
    const supabase = getSupabaseClient();

    void supabase.auth.getUser().then(({ data }) => {
      setUser(data.user ?? null);
      setIsAuthLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setIsAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const email = user?.email;

    if (!email) {
      setProfile(null);
      setIsProfileLoading(false);
      return;
    }

    let cancelled = false;
    setIsProfileLoading(true);

    void (async () => {
      await syncStaffRole();

      if (cancelled) {
        return;
      }

      const staffProfile = await findStaffProfileByEmail(email);

      if (cancelled) {
        return;
      }

      if (!staffProfile) {
        const supabase = getSupabaseClient();
        await supabase.auth.signOut();
        if (!cancelled) {
          setUser(null);
          setProfile(null);
          setIsProfileLoading(false);
        }
        return;
      }

      setProfile(staffProfile);
      setIsProfileLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, getUserRole(user)]);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }, []);

  const isLoading = isAuthLoading || (Boolean(user?.email) && isProfileLoading);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      isLoading,
      isStaff: user ? isStaffUser(user) && profile !== null : false,
      isAdmin: user ? isAdminUser(user) && profile !== null : false,
      signOut,
    }),
    [user, profile, isLoading, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
